import { LimeAlert } from "@/Components/NextBase/LimeAlert";
import { api } from "@/lib/api-client";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Chip,
  Divider,
  Link,
} from "@heroui/react";
import { i18n } from "@lingui/core";
import { Trans } from "@lingui/react/macro";
import { ClockAlert } from "lucide-react";

export const StripeStatus = () => {
  const { data: accountData } = api.stripe.useGetStripeAccount();

  const requirements = accountData?.requirements;

  if (!requirements) {
    return <></>;
  }

  return (
    <div className="flex flex-col gap-4">
      {requirements.isDisabled && (
        <LimeAlert
          title={<Trans>Stripe je deaktiviran</Trans>}
          description={
            <Trans>
              Vaš račun Stripe je bil onemogočen zaradi neporavnanih zahtev.
              Izpolnite vse zahteve spodaj, da ponovno aktivirate svoj račun.
            </Trans>
          }
        />
      )}

      {!requirements.isDisabled && requirements.hasDueRequirements && (
        <LimeAlert
          title={
            <Trans>
              Stripe račun je aktiven, vendar imate zahteve, ki jih je treba
              izpolniti.
            </Trans>
          }
          color="warning"
        />
      )}

      {!requirements.isDisabled && !requirements.hasDueRequirements && (
        <LimeAlert
          title={
            <Trans>Stripe račun je aktiven in vse zahteve so izpolnjene.</Trans>
          }
          color="success"
        />
      )}

      {requirements.pastDue != null && requirements.pastDue.length > 0 ? (
        <>
          <p className="flex items-center gap-2 text-lg font-medium text-danger">
            <Trans>Pretekle zahteve</Trans>
            <Chip color="danger" size="sm" className="px-2">
              <p className="font-bold">{requirements.pastDue.length}</p>
            </Chip>
          </p>

          <Card shadow="none" className="border-1 border-gray-200">
            <CardBody>
              {requirements.pastDue?.map((requirement) => (
                <Requirement
                  key={requirement.requirement}
                  requirement={requirement}
                />
              ))}
            </CardBody>
          </Card>
        </>
      ) : null}

      {requirements.currentlyDue != null &&
      requirements.currentlyDue.length > 0 ? (
        <>
          <p className="flex items-center gap-2 text-lg font-medium text-warning">
            <Trans>Trenutno zapadle zahteve</Trans>
            <Chip color="warning" size="sm" className="px-2" variant="flat">
              <p className="font-bold">{requirements.currentlyDue.length}</p>
            </Chip>
          </p>

          {requirements.currentDeadline ? (
            <div className="flex items-center gap-2">
              <ClockAlert size={16} className="text-warning" />
              <p className="text-sm">
                <Trans>Rok</Trans>:{" "}
                {new Date(requirements.currentDeadline).toLocaleDateString(
                  i18n.locale,
                )}
              </p>
            </div>
          ) : null}

          <Card shadow="none" className="border-1 border-gray-200">
            <CardBody>
              {requirements.currentlyDue?.map((requirement) => (
                <Requirement
                  key={requirement.requirement}
                  requirement={requirement}
                />
              ))}
            </CardBody>
          </Card>
        </>
      ) : null}

      {(requirements.pastDue != null && requirements.pastDue.length > 0) ||
      (requirements.currentlyDue != null &&
        requirements.currentlyDue.length > 0) ? (
        <>
          <Divider className="my-2" />

          <Card className="bg-gray-50 p-2" shadow="none">
            <CardHeader className="text-lg font-medium">
              <Trans>Izpolnite zahteve</Trans>
            </CardHeader>
            <CardBody>
              <p className="text-sm">
                <Trans>
                  Kliknite na gumb spodaj, da boste preusmerjeni na varno
                  potrditveno postopek Stripe. Boste vodeni skozi vsako zahtevo
                  korak za korakom. Ko bo zaključeno, bo status vašega računa
                  posodobljen samodejno.
                </Trans>
              </p>

              <div className="mt-4 rounded-lg bg-gray-100 p-3">
                <p className="mb-2 text-sm font-medium">
                  <Trans>Kaj se bo zgodilo:</Trans>
                </p>
                <ul className="list-disc pl-4 text-sm">
                  <li>
                    <Trans>Preusmerjeni boste na varno platformo Stripe.</Trans>
                  </li>
                  <li>
                    <Trans>Izpolnite zahteve po svojem tempu.</Trans>
                  </li>
                  <li>
                    <Trans>Posredujte potrebne dokumente in informacije.</Trans>
                  </li>
                  <li>
                    <Trans>
                      Po zaključku osvežite to stran, da vidite posodobljen
                      status.
                    </Trans>
                  </li>
                </ul>
              </div>
            </CardBody>
            <CardFooter>
              <Button
                as={Link}
                href={`https://dashboard.stripe.com/b/${accountData?.stripeAccountId}/account/status`}
                target="_blank"
                color="primary"
                className="mt-2"
                showAnchorIcon
              >
                <Trans>Izpolni zahteve na Stripe</Trans>
              </Button>
            </CardFooter>
          </Card>
        </>
      ) : null}
    </div>
  );
};

const Requirement = ({
  requirement,
}: {
  requirement: {
    requirement: string;
    label: string;
  };
}) => {
  if (requirement.requirement.includes("verification.document")) {
    return (
      <div>
        <p className="font-medium">{requirement.label}</p>
        <p className="text-sm text-gray-500">
          <Trans>ID zahteve</Trans>: {requirement.requirement}
        </p>
      </div>
    );
  }

  return <div>{requirement.label}</div>;
};
